import { eventChannel } from 'redux-saga';
import { all, call, put, select, spawn, take, takeEvery } from 'typed-redux-saga/macro';

import { debugApp as debug } from 'Lib/debug';
import { getRehydrateLifecycle, storageCleanup } from 'Lib/persist';
import { flush } from 'Lib/persist/actions';
import { REHYDRATE, REHYDRATED } from 'Lib/persist/constants';
import { GetActionByType } from 'Lib/reduxType';
import { addEventListener, removeEventListener } from 'Lib/utils/mediaQueryListener';

import { setThemeDevicePreference } from 'Store/appSettings/actions';
import { ThemeId } from 'Store/appSettings/types';
import { getIsRemote, getRemoteComponent } from 'Store/context/selectors';
import { loadLanguageSaga } from 'Store/internationalization/logic';
import { getCurrentLanguage } from 'Store/internationalization/selectors';

import { appInitializeComplete, appInitializeStart } from '../lifecycles';
import { handleLogAppOpen } from './logic/appOpen';
import { handleDOBViolation } from './logic/dobViolation';
import { registerLevers } from './logic/levers';
import { handleLoginFlow } from './logic/loginFlow';
import { handleTabWatch } from './logic/tabWatcher';
import { API_HELPER_INITIATED } from './constants';

/**
 * App Sagas
 */
export function* appInitializeStartSaga() {
  yield* call(appInitializeStart.block);
  yield* all([call(handleDOBViolation), call(handleLoginFlow), call(registerLevers)]);
}

export function* appInitializeCompleteSaga() {
  yield* call(appInitializeComplete.block);
  yield* all([call(handleTabWatch), call(handleLogAppOpen)]);
}

export function* persistRootSaga() {
  yield* takeEvery(REHYDRATE, function* rehydrateSaga(action: GetActionByType<typeof REHYDRATE>) {
    yield* call(getRehydrateLifecycle(action.payload.key).trigger);
  });

  yield* takeEvery(REHYDRATED, function* rehydratedSaga() {
    yield* call(getRehydrateLifecycle().trigger);
  });

  // Flush any pending persists before we unload the page
  yield* spawn(function* flushBeforeUnloadSaga() {
    const channel = eventChannel((emit) => {
      window.addEventListener('beforeunload', emit);
      return () => {
        window.removeEventListener('beforeunload', emit);
      };
    });

    while (true) {
      yield* take(channel);
      yield* put(flush());
      channel.close();
    }
  });
  yield* put(flush());
}

export function* deviceThemePreferenceSaga() {
  const mediaQuery = window.matchMedia(`(prefers-color-scheme: dark)`);
  yield* put(setThemeDevicePreference(mediaQuery.matches ? ThemeId.Dark : ThemeId.Light));

  const channel = eventChannel((emit) => {
    const matchesEmitter = (ev) => {
      emit(ev.matches ? ThemeId.Dark : ThemeId.Light);
    };
    addEventListener(mediaQuery, matchesEmitter);
    return () => removeEventListener(mediaQuery, matchesEmitter);
  });

  try {
    while (true) {
      const themePref = yield* take(channel);
      yield* put(setThemeDevicePreference(themePref as ThemeId));
    }
  } finally {
    channel.close();
  }
}

export function* appInitializeSaga() {
  yield* all([
    take(API_HELPER_INITIATED), // API Token
    call(getRehydrateLifecycle().block), // Wait for everything to rehydrate
  ]);

  const isRemote = yield* select(getIsRemote);
  const remoteComponent = yield* select(getRemoteComponent);
  const locale = yield* select(getCurrentLanguage);

  // RemoteView can skip these sagas
  if (!isRemote || remoteComponent === 'AppealsCenter') {
    // App initialization lifecycle hooks
    yield* spawn(appInitializeStartSaga);
    yield* spawn(appInitializeCompleteSaga);
  }

  debug('app initialize start');
  yield* call(appInitializeStart.trigger);

  // Critical app initialization actions
  // App will render after language loaded,
  // app logic will needs to be registered first
  yield* call(loadLanguageSaga, { payload: locale });

  yield* spawn(deviceThemePreferenceSaga);

  debug('app initialize complete');
  yield* call(appInitializeComplete.trigger);

  yield* call(storageCleanup);
}
